import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { JOURNAL_EPOCH_ITEMS, type JournalEntry, type ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { ArriveButton } from '../components/ArriveButton';
import { DepartButton } from '../components/DepartButton';
import { ServiceDoneButton } from '../components/ServiceDoneButton';
import { ServicesForm } from '../components/ServicesForm';
import { KeycafeCard } from '../components/KeycafeCard';
import { WhatsAppCard } from '../components/WhatsAppCard';
import { EditForm } from '../components/EditForm';
import { StatusButtons } from '../components/StatusButtons';
import { PrepaidForm } from '../components/PrepaidForm';
import { DeskPayForm } from '../components/DeskPayForm';
import { StavRow } from '../components/StavRow';
import { RescheduleForm } from '../components/RescheduleForm';
import {
  CZK,
  LOCATION_LABEL,
  PAYMENT_LABEL,
  STATUS_CLASS,
  STATUS_LABEL,
  TYPE_LABEL, TYPE_SHORT,
  dayWord,
  spotLabel,
} from '../lib/format';
import { isEmail, isPhone, longDt } from '../lib/filters';
import { Footer } from '../components/Footer';

/**
 * The reservation detail.
 *
 * The grid answers "which bookings", this answers "this booking". The legacy
 * extranet put the same information behind a click on the row, in roughly
 * this order, and dispatchers read it top-down while on the phone: who it is,
 * when they come and go, what car, where it stands, what they owe. Keeping
 * the order is the point — the sections are the same, only the typography is
 * new.
 *
 * Phase 1 put the first action on it — Přijel — and the change history
 * underneath, the legacy journal, so what the button did is visible on the
 * same screen a minute later. There are still no other save buttons,
 * because there is nothing behind them yet, and a disabled save button
 * that never becomes enabled is worse than no button at all.
 */

/** The legacy column names, as a dispatcher would say them. */
const ITEM_LABEL: Record<string, string> = {
  status: 'Stav',
  arrival: 'Příjezd — plán',
  departure: 'Odjezd — plán',
  arrival_real: 'Příjezd — skutečný',
  departure_real: 'Odjezd — skutečný',
  parking: 'Stání',
  parking_location: 'Lokalita',
  parkingtarget: 'Doporučené místo',
  keycafecode_arrival: 'Keycafe příjezd',
  keycafecode_departure: 'Keycafe odjezd',
  transfer_e2t: 'Převoz z letiště',
  transfer_t2e: 'Převoz na letiště',
  price: 'Cena',
  paid: 'Zaplaceno',
  payment: 'Způsob platby',
  note: 'Poznámka',
  spz: 'SPZ',
  car: 'Auto',
  drivername: 'Jméno',
  driverphone: 'Telefon',
  driveremail: 'E-mail',
  flightnumber: 'Číslo letu',
  reslock: 'Zámek',
  /* The key (the dispatchers' call of 30 Oct 2026): the customer's check-in button and the KeyCafe wall. */
  checkin_keydropoff: 'Klient: Klíč vložen',
  keycafe_dropoff: 'KeyCafe: klíč vložen',
  keycafe_pickup: 'KeyCafe: klíč vyzvednut',
};

/**
 * `reservation_journal.changed` is a TIMESTAMP column and comes over as UTC
 * (the API pins its MySQL sessions to UTC; the staging file is a --tz-utc
 * dump). Every other time on this screen is Prague wall-clock already.
 */
function utcToLocal(v: string): string {
  if (!v || v.length < 19) return '—';
  const d = new Date(`${v.slice(0, 10)}T${v.slice(11, 19)}Z`);
  if (!Number.isFinite(d.getTime())) return v;
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * A journal value, readable. The datetime columns are journaled as unix
 * seconds — the legacy convention — and come back as a Prague time here,
 * which is the browser's zone on every desk this runs on.
 */
function journalValue(item: string, v: string, userName?: string | null): React.ReactNode {
  if (v === '') return <span className="empty">—</span>;
  // A user id with its name resolved by the server (the drivers of a
  // transfer, the partner): "Almaz (#358)", not a bare number.
  if (userName) return `${userName} (#${v})`;
  if (JOURNAL_EPOCH_ITEMS.has(item) && /^\d{9,11}$/.test(v)) {
    const d = new Date(Number(v) * 1000);
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  if (item === 'status') return STATUS_LABEL[v] ?? v;
  if (item === 'parking_location') return LOCATION_LABEL[v] ?? v;
  if (item === 'payment') return PAYMENT_LABEL[v] ?? v;
  return v;
}

/**
 * "Doplňkové služby" — the ordered services with the legacy form's tick
 * beside each job, and "Upravit služby" opening the old edit form's
 * checklist (ServicesForm) to add, remove or reprice. Valet is listed as
 * the legacy listed it: a row with no price and no job to tick.
 */
function Sluzby({ id, active }: { id: number; active: boolean }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['reservation-services', id],
    queryFn: () => api.reservationServices(id),
    retry: false,
  });
  const [editing, setEditing] = useState(false);
  const ordered = (data?.services ?? []).filter((s) => s.ordered);
  return (
    <section className="card detailcard">
      <h2>Doplňkové služby</h2>
      {isPending ? (
        <div className="empty">Načítám…</div>
      ) : isError || !data ? (
        <div className="empty">Služby se nepodařilo načíst.</div>
      ) : editing ? (
        <ServicesForm reservationId={id} lines={data.services} onClose={() => setEditing(false)} />
      ) : (
        <>
          {ordered.length === 0 ? (
            <div className="empty">Bez objednaných služeb.</div>
          ) : (
            <table className="mini">
              <tbody>
                {ordered.map((s) => (
                  <tr key={s.id}>
                    <td>{s.title}</td>
                    <td className="num">{s.price > 0 ? `${CZK.format(s.price)} Kč` : <span className="empty">—</span>}</td>
                    <td>
                      {s.id === VALET_SERVICE_ID ? (
                        <span className="empty">převoz</span>
                      ) : s.done ? (
                        <span className="arrived-mark">✔ hotovo {longDt(s.done)}</span>
                      ) : active ? (
                        <ServiceDoneButton reservationId={id} serviceId={s.id} compact />
                      ) : (
                        <span className="empty">neprovedeno</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {active && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn sm" onClick={() => setEditing(true)}>
                Upravit služby
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** The valet product is a service row in the schema but not a job on the yard. */
const VALET_SERVICE_ID = 2;

/* The photos the old booking page shows (30 October 2026: "the new system shows no photos,
   neither the customer's nor the drivers'"), grouped and titled as there. */
const PHOTO_KINDS: Array<[string, string]> = [
  ['client', 'Fotografie od klienta'],
  ['dashboardclient', 'Fotografie klienta (dispečink)'],
  ['e2t', 'Fotografie řidiče z převozu do Tuchoměřic'],
  ['tuchomerice', 'Zaparkováno v Tuchoměřicích'],
  ['dashboard', 'Fotografie z dispečinku'],
  ['tuchomericecheck', 'Kontrola v Tuchoměřicích'],
  ['t2e', 'Fotografie řidiče z převozu na Europort'],
  ['europort', 'Zaparkováno na Europortu'],
];

function Photos({ id }: { id: number }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['photos', id],
    queryFn: () => api.photos(id),
    retry: false,
    staleTime: 60_000,
  });
  if (isPending) return null;
  if (isError || !data) return null;
  const groups = PHOTO_KINDS.map(([kind, title]) => [title, data.photos.filter((p) => p.kind === kind)] as const).filter(([, ps]) => ps.length > 0);
  return (
    <section className="card detailcard photos">
      <h2>Fotografie</h2>
      {!data.available ? (
        <div className="empty">Fotografie se nepodařilo načíst.</div>
      ) : groups.length === 0 ? (
        <div className="empty">K této rezervaci zatím nejsou žádné fotografie.</div>
      ) : (
        groups.map(([title, ps]) => (
          <div key={title} className="photogroup">
            <h3>
              {title} <span className="empty">({ps.length})</span>
            </h3>
            <div className="photogrid">
              {ps.map((p) => (
                <a key={p.file} href={p.url} target="_blank" rel="noopener" title={p.at ? `Datum: ${longDt(p.at)}` : p.file}>
                  <img src={p.url} alt={title} loading="lazy" />
                  {p.at && <span>{longDt(p.at)}</span>}
                </a>
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

function Journal({ id }: { id: number }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['journal', id],
    queryFn: () => api.journal(id),
    retry: false,
  });
  return (
    <section className="card detailcard journal">
      <h2>Historie změn</h2>
      {isPending ? (
        <div className="empty">Načítám…</div>
      ) : isError || !data ? (
        <div className="empty">Historii se nepodařilo načíst.</div>
      ) : data.entries.length === 0 ? (
        <div className="empty">Zatím žádné změny.</div>
      ) : (
        <table className="mini">
          <thead>
            <tr>
              <th>Kdy</th>
              <th>Kdo</th>
              <th>Co</th>
              <th>Původně</th>
              <th>Nově</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e: JournalEntry, i: number) => (
              <tr key={`${e.changed}-${e.item}-${i}`}>
                <td className="mono">{utcToLocal(e.changed)}</td>
                <td>{e.userName || (e.userId ? `#${e.userId}` : 'systém')}</td>
                <td>{ITEM_LABEL[e.item] ?? e.item}</td>
                <td className="was">{journalValue(e.item, e.oldValue, e.oldUserName)}</td>
                <td>{journalValue(e.item, e.newValue, e.newUserName)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'field wide' : 'field'}>
      <dt>{label}</dt>
      <dd>{children ?? <span className="empty">—</span>}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card detailcard">
      <h2>{title}</h2>
      <dl className="fields">{children}</dl>
    </section>
  );
}

const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? <span className="empty">—</span> : v;

export function RezervaceDetail() {
  const { id: raw } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = Number(raw);
  const valid = Number.isInteger(id) && id > 0;

  /* `?upravit=1` opens the page with the edit form already open — the row
     drawer's "Opravit údaje" link (22 October 2026: the manager looked for
     the car fields in the row and never found the button on this page). */
  const [sp] = useSearchParams();
  const [editing, setEditing] = useState(sp.get('upravit') === '1');
  const [rescheduling, setRescheduling] = useState(false);
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => api.reservation(id),
    enabled: valid,
    retry: false,
  });

  if (!valid) {
    return <Missing id={raw ?? ''} />;
  }

  if (isPending) {
    return (
      <div className="container">
        <div className="pagehead">
          <h1>Rezervace #{id}</h1>
        </div>
        <div className="card detailcard">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="skeleton" style={{ margin: '10px 0', height: 15 }} />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    const notFound = error instanceof ApiError && error.status === 404;
    return notFound ? (
      <Missing id={String(id)} />
    ) : (
      <div className="container">
        <BackLink />
        <div className="card state" role="alert">
          Rezervaci se nepodařilo načíst
          {error instanceof Error && error.message ? ` (${error.message})` : ''}.{' '}
          <button className="btn sm" type="button" onClick={() => navigate(0)}>
            Zkusit znovu
          </button>
        </div>
      </div>
    );
  }

  const r: ReservationRow = data;
  const balanceCritical = r.outstanding > 0;

  return (
    <div className="container">
      <BackLink />

      <div className="pagehead">
        <h1>
          <span className={STATUS_CLASS[r.status] ?? 'stav gray'}>
            {STATUS_LABEL[r.status] ?? r.status}
          </span>{' '}
          Rezervace #{r.id}
        </h1>
        <span className="sub">
          {TYPE_SHORT[r.type] ?? r.type} · {r.days} {dayWord(r.days)}
        </span>
        <div className="acts">
          {isEmail(r.driverEmail) && (
            <a className="btn" href={`mailto:${r.driverEmail}`}>
              Napsat e-mail
            </a>
          )}
          {isPhone(r.driverPhone) && (
            <a className="btn" href={`tel:${r.driverPhone.replace(/[^\d+]/g, '')}`}>
              Zavolat
            </a>
          )}
          {/* The handover sheet, the legacy "Prot." — in a new tab, which
              prints itself and closes. */}
          <a className="btn" href={`/protokol/${r.id}`} target="_blank" rel="noopener">
            Tisk protokolu
          </a>
          {!['canceled', 'noshow', 'deleted'].includes(r.status) && (
            <a className="btn" href={`/uctenka/${r.id}`} target="_blank" rel="noopener">
              Účtenka
            </a>
          )}
          {r.status === 'closed' && (
            <>
              <a className="btn" href={`/dobropis/${r.id}`} target="_blank" rel="noopener" title="STORNO celé účtenky">
                Dobropis
              </a>
              {r.servicesPrice > 0 && (
                <a className="btn sm" href={`/dobropis/${r.id}?druh=services`} target="_blank" rel="noopener" title="STORNO jen služeb">
                  jen služby
                </a>
              )}
              {r.penalty > 0 && (
                <a className="btn sm" href={`/dobropis/${r.id}?druh=price_penalty`} target="_blank" rel="noopener" title="STORNO jen pokuty">
                  jen pokuta
                </a>
              )}
            </>
          )}
          <ArriveButton r={r} />
          <DepartButton r={r} />
          <StatusButtons r={r} />
          {r.status !== 'deleted' && !editing && (
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Opravit údaje
            </button>
          )}
          {['new', 'pending', 'paid', 'verified'].includes(r.status) && !rescheduling && (
            <button type="button" className="btn" onClick={() => setRescheduling(true)}>
              Změnit termín
            </button>
          )}
        </div>
      </div>

      {editing && <EditForm key={r.id} r={r} onClose={() => setEditing(false)} />}
      {rescheduling && <RescheduleForm key={`r${r.id}`} r={r} onClose={() => setRescheduling(false)} />}

      <div className="detailgrid">
        <Section title="Klient">
          <Field label="Jméno">{dash(r.driverName)}</Field>
          <Field label="Telefon">
            {isPhone(r.driverPhone) ? (
              <a href={`tel:${r.driverPhone.replace(/[^\d+]/g, '')}`}>{r.driverPhone}</a>
            ) : (
              dash(r.driverPhone)
            )}
          </Field>
          <Field label="E-mail" wide>
            {isEmail(r.driverEmail) ? (
              <a href={`mailto:${r.driverEmail}`}>{r.driverEmail}</a>
            ) : (
              // Not a link: the column is free text and holds plenty of
              // things that are not addresses.
              dash(r.driverEmail)
            )}
          </Field>
          <Field label="Klub">
            {r.clubTier ? (
              <span className={`flag ${r.clubTier === 'platinum' ? 'plat' : 'gold'}`}>
                {r.clubTier.toUpperCase()}
              </span>
            ) : (
              <span className="empty">bez členství</span>
            )}
          </Field>
          <Field label="Osob / sedačky">
            {dash(r.person)} / {r.babycarseat ?? 0}
          </Field>
        </Section>

        <Section title="Termín">
          <Field label="Příjezd — plán">{longDt(r.arrival)}</Field>
          <Field label="Příjezd — skutečný">
            {r.arrivalReal ? longDt(r.arrivalReal) : <span className="empty">nepřijel / nezapsáno</span>}
          </Field>
          <Field label="Odjezd — plán">{longDt(r.departure)}</Field>
          <Field label="Odjezd — skutečný">
            {r.departureReal ? longDt(r.departureReal) : <span className="empty">neodjel / nezapsáno</span>}
          </Field>
          <Field label="Účtované dny">{r.days}</Field>
          <Field label="Vytvořeno">{longDt(r.created)}</Field>
          <Field label="Číslo letu">{dash(r.flightNumber)}</Field>
        </Section>

        <Section title="Vozidlo a stání">
          <Field label="Auto">
            {r.car || <span className="doplnit">Doplnit auto!</span>}
          </Field>
          <Field label="SPZ">
            {r.spz ? <span className="spz">{r.spz}</span> : <span className="doplnit">Doplnit SPZ!</span>}
          </Field>
          <Field label="Stání">{dash(spotLabel(r.parking))}</Field>
          <Field label="Lokalita">
            {r.parkingLocation
              ? LOCATION_LABEL[r.parkingLocation] ?? r.parkingLocation
              : dash(null)}
          </Field>
          <Field label="Doporučené místo">{dash(spotLabel(r.parkingTarget))}</Field>
          <Field label="Keycafe příjezd">{dash(r.keycafeArrival)}</Field>
          <Field label="Keycafe odjezd">{dash(r.keycafeDeparture)}</Field>
        </Section>

        <Section title="Platba">
          <Field label="Cena">
            <b>{CZK.format(r.price)} Kč</b>
            {/* `price_original` is the legacy's total at creation — parking
                plus the services ordered — not an earlier parking price. */}
            {r.priceOriginal !== null && r.priceOriginal !== r.price && (
              <span className="was"> (při vytvoření vč. služeb {CZK.format(r.priceOriginal)} Kč)</span>
            )}
          </Field>
          <Field label="Zaplaceno">{CZK.format(r.paid)} Kč</Field>
          <Field label="Zbývá">
            {balanceCritical ? (
              <span className="badge-pen">{CZK.format(r.outstanding)} Kč</span>
            ) : (
              <span className="empty">0 Kč</span>
            )}
          </Field>
          <Field label="Způsob platby">
            {r.payment ? PAYMENT_LABEL[r.payment] ?? r.payment : dash(null)}
          </Field>
          <div className="field wide">
            <DeskPayForm r={r} />
          </div>
          <div className="field wide">
            <PrepaidForm r={r} />
          </div>
          {/* The old form's "GoPay · Online platba" line: the public site's
              pay page for this booking, to read out or send to a customer
              who wants to pay before arriving. Computed by the server from
              the booking number as the old code did; the page is the old
              site's, untouched. */}
          {r.payOnlineUrl && !['closed', 'canceled', 'noshow', 'deleted'].includes(r.status) && (
            <Field label="GoPay · Online platba" wide>
              <a href={r.payOnlineUrl} target="_blank" rel="noopener">
                {r.payOnlineUrl.replace(/^https?:\/\//, '')}
              </a>
            </Field>
          )}
        </Section>

        <Section title="Převozy">
          {/* Time and driver of each leg (the manager, 22 October 2026: "only
              the date and time, nothing about the drivers"). The GPS unit is
              not shown: 1 transfer in 77 000 since 2024 carries one, the
              driver app does not store it, and the manager went looking for
              it (26 October). The dispatch page keeps its GPS column, as the
              legacy page has it. */}
          <Field label="Na parkoviště (e2t)">
            {r.transferE2t ? (
              <>
                {longDt(r.transferE2t)}
                {r.transferE2tDriver && <> · řidič <b>{r.transferE2tDriver}</b></>}
              </>
            ) : (
              <span className="empty">nevypraven</span>
            )}
          </Field>
          <Field label="Na letiště (t2e)">
            {r.transferT2e ? (
              <>
                {longDt(r.transferT2e)}
                {r.transferT2eDriver && <> · řidič <b>{r.transferT2eDriver}</b></>}
              </>
            ) : (
              <span className="empty">nevypraven</span>
            )}
          </Field>
          {/* The driver app's links, as the legacy booking page prints them (the owner, 30 October 2026:
              "how come we don't have these links?") — the drivers open the booking's leg from them. */}
          <Field label="Převoz do Tuchoměřic" wide>
            <a href={`https://ridic.smileparking.cz/e2t/${r.id}`} target="_blank" rel="noopener">
              ridic.smileparking.cz/e2t/{r.id}
            </a>
          </Field>
          <Field label="Převoz na Europort" wide>
            <a href={`https://ridic.smileparking.cz/t2e/${r.id}`} target="_blank" rel="noopener">
              ridic.smileparking.cz/t2e/{r.id}
            </a>
          </Field>
        </Section>

        <Section title="Původ a poznámka">
          <Field label="Zadal">
            {r.partnerName ? `${r.partnerName}${r.partnerId ? ` (#${r.partnerId})` : ''}` : dash(null)}
          </Field>
          <Field label="Reference partnera">
            {/* `partnerid` in the legacy schema. Despite the name it is an
                external string, not a foreign key — never joined on. */}
            {dash(r.partnerRef)}
          </Field>
          <Field label="Poznámka" wide>
            {r.note ? <span className="note">{r.note}</span> : dash(null)}
          </Field>
          <Field label="Fakturace na" wide>
            {r.invoiceCompany
              ? `${r.invoiceCompany}, ${[r.invoiceStreet, r.invoiceCity, r.invoicePsc].filter(Boolean).join(', ')}${r.invoiceIc ? ` · IČ ${r.invoiceIc}` : ''}${r.invoiceDic ? ` · DIČ ${r.invoiceDic}` : ''}`
              : dash(null)}
          </Field>
        </Section>
      </div>

      <div className="detailgrid" style={{ marginTop: 14 }}>
        <Sluzby id={r.id} active={['new', 'pending', 'paid', 'verified'].includes(r.status)} />

        <KeycafeCard r={r} />
      </div>

      <div className="detailgrid" style={{ marginTop: 14 }}>
        <WhatsAppCard r={r} />
      </div>

      {/* The legacy edit form's "Nastavení → Stav" strip, above the history
          as there. */}
      <section className="card detailcard">
        <h2>Nastavení</h2>
        <StavRow r={r} />
      </section>

      <Photos id={r.id} />

      <Journal id={r.id} />

      <Footer />
    </div>
  );
}

function BackLink() {
  return (
    <Link className="backlink" to="/rezervace">
      ‹ Zpět na rezervace
    </Link>
  );
}

function Missing({ id }: { id: string }) {
  return (
    <div className="container">
      <BackLink />
      <div className="pagehead">
        <h1>Rezervace nenalezena</h1>
      </div>
      <div className="card state">
        Rezervace {id ? <b>#{id}</b> : null} v databázi není. Zkontrolujte
        číslo — nebo ji hledejte podle jména či SPZ v seznamu rezervací.
      </div>
    </div>
  );
}
